import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import GuideMagnet from "@/components/sections/GuideMagnet";
import Seo, { buildFaqSchema } from "@/components/Seo";
import SpainFaq, { useFaqItems } from "@/components/sections/SpainFaq";
import { WaitlistForm } from "@/components/WaitlistForm";
import { ArticleCard } from "@/components/ArticleCard";
import { GuideCta } from "@/components/GuideCta";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { HOME } from "@/lib/homeContent";
import { usePersonalization, orderResources } from "@/lib/personalization";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };

export default function Resources() {
  const [posts, setPosts] = useState([]);
  const { section } = useSiteContent();
  const ctasCopy = section("conversion_ctas", HOME.conversion_ctas);
  const wl = section("waitlist", HOME.waitlist);
  const { items: faqItems } = useFaqItems();
  const { profile, need, p } = usePersonalization();
  const ordered = orderResources(posts, { profile, need });
  const startHere = profile ? ordered.filter((x) => (x.persona_tags || []).includes(profile)).slice(0, 3).map((x) => x.slug) : [];

  useEffect(() => {
    axios.get(`${API}/posts`).then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => setPosts([]));
  }, []);

  return (
    <main data-testid="resources-page" className="min-h-screen bg-[#F5EFE7] text-[#451C25]">
      <Seo title="Resources for moving to Spain from the U.S. | Destino Europe" description="Our free guide, practical articles and answers to common questions for Americans planning a move to Spain." path="/resources" jsonLd={buildFaqSchema(faqItems)} />
      <Header />

      <section className="pt-40 pb-14 px-6 md:px-10 lg:px-14">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#897756] text-[11px] tracking-[0.3em] uppercase mb-6 font-semibold" style={K}>Resources</p>
          <h1 data-testid="resources-headline" className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" style={{ ...K, fontWeight: 600 }}>
            Resources for the road ahead.
          </h1>
          <p className="mt-6 max-w-2xl text-[#6B4954] text-lg leading-relaxed" style={S}>
            Start with the free guide. Then dig into the articles and answers that match where you are in the process.
          </p>
        </div>
      </section>


      <section data-testid="resources-articles" className="py-20 md:py-28 px-6 md:px-10 lg:px-14">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#897756] text-[11px] tracking-[0.3em] uppercase mb-5 font-semibold" style={K}>Spain resources</p>
          <h2 data-testid="resources-articles-title" className="text-3xl md:text-4xl tracking-tight" style={{ ...K, fontWeight: 600 }}>{profile ? p.resources_personal_title : p.resources_neutral_title}</h2>
          {posts.length === 0 ? (
            <p data-testid="resources-empty" className="mt-8 text-[#6B4954]" style={S}>New articles are on the way. In the meantime, the guide above is the best place to start.</p>
          ) : (
            <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-14">
              {ordered.map((p, i) => (
                <li key={p.slug}><ArticleCard post={p} eager={i < 3} startHere={startHere.includes(p.slug)} testId={`resource-post-${p.slug}`} /></li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section data-testid="resources-start-guide" className="bg-[#F5EFE7] text-[#451C25] px-6 md:px-10 lg:px-14 pb-20 md:pb-24">
        <div className="max-w-7xl mx-auto border-t border-[#451C25]/15 pt-12">
          <GuideCta placement="resources_end" prompt={ctasCopy.resources_prompt} heading={ctasCopy.resources_heading} className="max-w-2xl" />
        </div>
      </section>

      <div data-testid="resources-featured">
        <GuideMagnet />
      </div>

      <SpainFaq />

      <section id="waitlist" data-testid="resources-waitlist" className="border-t border-[#451C25]/15 py-16 md:py-20 px-6 md:px-10 lg:px-14">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="text-[#897756] text-[11px] tracking-[0.3em] uppercase mb-4 font-semibold" style={K}>{wl.eyebrow}</p>
            <h2 className="text-2xl md:text-3xl tracking-tight" style={{ ...K, fontWeight: 500 }}>{wl.headline}</h2>
            <p className="mt-4 text-[#6B4954] text-[15px] leading-relaxed" style={S}>{wl.text}</p>
          </div>
          <div className="md:col-span-7">
            <WaitlistForm copy={wl} />
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
